// Player + PlayerMgr — credits/power/upgrades/unit counts + the PLYR hash group (18 sites).
// game/Player.cpp + game/ht.h:5626-5980. Out of scope here: lag-state machine (dead
// client-side), SendWinStats/ObjectivesForm (UI), SP-level Init (lands with Level),
// Stream load/save (lands with the savegame port).

import { quad } from '../hash/quads.ts';
import { StateFrame } from '../hash/stateframe.ts';
import type { SimContext } from '../context.ts';
import { divfx, itofx } from '../math/fix.ts';
import { Gob, kfGobActive, kfGobStructure, kfGobUnit } from '../gob/gob.ts';
import type { PlayerLike } from '../gob/gob.ts';
import { kgtHumanResourceCenter, kgtVehicleTransportStation } from '../gob/gobtypes.ts';
import {
  kmidBeingUpgraded,
  kmidPowerLowHigh,
  kmidUpgradeComplete,
  kfUpgradeHrc,
  kfUpgradeHrcInProgress,
  kfUpgradeVts,
  kfUpgradeVtsInProgress,
} from '../sm/message.ts';
import { GetSideMask } from '../types.ts';
import {
  kcPlayersMax,
  kutMax,
  kutProcessor,
  kutWarehouse,
  kfPlrInUse,
  kfPlrComputer,
  kfPlrObserver,
  kfPlrAutoRepair,
  kfPlrReady,
  kfPlrComputerOvermind,
  kfPlrUnfulfilled,
  kfPlrCreator,
  kfHcapHard,
  kfHcapDefault,
  knLagNone,
  knConsumerMax,
  knConsumerGeneric,
  knProcessorCapacity,
  knWarehouseCapacity,
  ksideNeutral,
  kcSides,
  knIntelligenceComputer,
  knIntelligenceComputerOvermind,
  knIntelligenceComputerNeutral,
  kupgmAdvancedHRC,
  kupgmAdvancedVTS,
} from './side.ts';

// game/Player.cpp:9
const knCreditDelta = 18;

// game/ht.h:5578 — re-exported from masks.ts (the REAL value: infantry|vehicles|structures,
// NOT 0xffffffff — that's kumImpossible).
export { kumAll } from './masks.ts';
import { kumAll } from './masks.ts';
export const kupgmAll =
  (kupgmAdvancedHRC | kupgmAdvancedVTS | /* kupgmIncreasedBullpupSpeed */ 4) >>> 0;

/** The UnitGob surface Player's notify loops touch (full UnitGob lands with Unit.cpp). */
interface UnitGobLike extends Gob {
  GetConsts(): { um: number; wf: number; GetArmorStrength(): number };
  GetUnitFlags(): number;
  GetHealth(): number;
  Repair(fOn: boolean): void;
}

// UnitConsts flags re-exported from unitconsts.ts; unit-state flags from masks.ts
// (kfUnitRepairing = 0x0008 — the earlier VERIFY placeholder 0x0002 was wrong: that is
// kfUnitDrawHealthIndicator).
export { kfUntcNotifyPowerLowHigh } from '../gob/unitconsts.ts';
import { kfUntcNotifyPowerLowHigh } from '../gob/unitconsts.ts';
export { kfUnitRepairing } from './masks.ts';
import { kfUnitRepairing } from './masks.ts';

export class Player {
  private ctx: SimContext;

  m_wfUpgrades = 0;
  m_side = ksideNeutral;
  m_sidmAllies = 0;
  m_sidmDiscovered = 0;
  m_tptDiscover = { tx: 0, ty: 0 };
  m_szName = '';
  m_wf = 0;
  m_fBot = false;
  m_pid = 0;
  m_nCredits = 0;
  m_nCreditsAcquired = 0;
  m_nCreditsConsumed = 0;
  m_nDirCredits = 0;
  m_nConsumerCredits = knConsumerMax;
  m_nPowerSupply = 0;
  m_nPowerDemand = 0;
  m_acut = new Array<number>(kutMax).fill(0);
  m_acutBuilt = new Array<number>(kutMax).fill(0);
  m_umAllowed = kumAll;
  m_szObjective = '';
  m_upgm = 0;
  m_upgmAllowed = kupgmAll;
  m_szFormalObjectiveInfo: string | null = null;
  m_cUpdatesRepairLast = 0;
  m_cStructsNeedCredits = 0;
  // issue #96 / #98: a COMPLETE snapshot saves the true m_cStructsNeedCredits, but loading the
  // gobs afterwards re-runs each structure's ModifyNeedCreditsCount(+1) registration, double-
  // counting past the saved value. Stash the loaded value and re-apply it once gobs are placed.
  private m_loadedNeedCredits: number | null = null;
  m_wfHandicap = kfHcapDefault;
  m_cUpdates = -1;
  m_nLagState = knLagNone;
  m_fLeftGame = false;
  m_cmuntKilled = 0;
  m_cstruKilled = 0;
  m_cmuntLost = 0;
  m_cstruLost = 0;
  m_nTotalCreditsAcquired = 0;
  m_nTotalCreditsConsumed = 0;

  /** The PlayerMgr::Reset memset image: every field to its zeroed-memory value
   *  (NOT the Init(0) values — Init runs again only when a slot is allocated). */
  ZeroAsReset(): void {
    this.m_wf = 0;
    this.m_pid = 0;
    this.m_side = 0;
    this.m_szName = '';
    this.m_fBot = false;
    this.m_nCredits = 0;
    this.m_nCreditsAcquired = 0;
    this.m_nCreditsConsumed = 0;
    this.m_nDirCredits = 0;
    this.m_nConsumerCredits = 0; // Init(0) would set knConsumerMax
    this.m_nPowerSupply = 0;
    this.m_nPowerDemand = 0;
    this.m_acut.fill(0);
    this.m_acutBuilt.fill(0);
    this.m_umAllowed = 0; // Init(0) would set kumAll
    this.m_szObjective = '';
    this.m_upgm = 0;
    this.m_upgmAllowed = 0; // Init(0) would set kupgmAll
    this.m_szFormalObjectiveInfo = null;
    this.m_aszFormalObjectives.fill(null);
    this.m_aszFormalObjectiveStatus.fill(null);
    this.m_cUpdatesRepairLast = 0;
    this.m_cStructsNeedCredits = 0;
    this.m_wfHandicap = 0; // Init(0) would set kfHcapDefault — THE damage-scaling bit
    this.m_cUpdates = 0; // Init(0) would set -1
    this.m_nLagState = 0;
    this.m_fLeftGame = false;
    this.m_sidmAllies = 0;
    this.m_sidmDiscovered = 0;
    this.m_tptDiscover.tx = 0;
    this.m_tptDiscover.ty = 0;
    this.m_wfUpgrades = 0;
    this.m_cmuntKilled = 0;
    this.m_cstruKilled = 0;
    this.m_cmuntLost = 0;
    this.m_cstruLost = 0;
    this.m_nTotalCreditsAcquired = 0;
    this.m_nTotalCreditsConsumed = 0;
  }

  constructor(ctx: SimContext) {
    // game/Player.cpp:444-447 — ctor runs Init(0)
    this.ctx = ctx;
    this.Init(0);
  }

  // game/Player.cpp:485-521
  Init(pid: number): void {
    this.m_wf = 0;
    this.m_pid = pid;
    this.m_nCredits = 0;
    this.m_nCreditsConsumed = this.m_nCreditsAcquired = 0;
    this.m_nDirCredits = 0;
    this.m_nConsumerCredits = knConsumerMax;
    this.m_side = ksideNeutral;
    this.m_wfUpgrades = 0;
    this.m_nPowerDemand = 0;
    this.m_nPowerSupply = 0;
    this.m_szName = '';
    this.m_sidmAllies = 0;
    this.m_umAllowed = kumAll;
    this.m_szObjective = '';
    this.m_tptDiscover = { tx: 0, ty: 0 };
    this.m_upgm = 0;
    this.m_upgmAllowed = kupgmAll;
    this.m_szFormalObjectiveInfo = null;
    this.m_cUpdatesRepairLast = 0;
    this.m_cStructsNeedCredits = 0;
    this.m_wfHandicap = kfHcapDefault;
    this.m_cUpdates = -1;
    this.m_nLagState = knLagNone;
    this.m_fLeftGame = false;
    this.m_fBot = false;
    this.m_acut.fill(0);
    this.m_acutBuilt.fill(0);
    // (m_sidmDiscovered is zeroed by PlayerMgr::Reset's memset; Init leaves it — but the
    // ctor path runs after a fresh zeroed object, faithfully 0 here.)
    this.m_sidmDiscovered = 0;
    this.m_cmuntKilled = this.m_cstruKilled = this.m_cmuntLost = this.m_cstruLost = 0;
    this.m_nTotalCreditsAcquired = this.m_nTotalCreditsConsumed = 0;
  }

  // game/Player.cpp:463-483 — the PLYR hash group (18 tracked values)
  TrackState(frame: StateFrame): void {
    const i = frame.AddCountedValue(quad('PLYR'));
    frame.AddValue(quad('WFUP'), this.m_wfUpgrades, i);
    frame.AddValue(quad('SIDE'), this.m_side, i);
    frame.AddValue(quad('SALI'), this.m_sidmAllies, i);
    frame.AddValue(quad('SDIS'), this.m_sidmDiscovered, i);
    frame.AddValue(quad('CRED'), this.m_nCredits, i);
    frame.AddValue(quad('POWR'), this.m_nPowerSupply, i);
    frame.AddValue(quad('DMND'), this.m_nPowerDemand, i);
    frame.AddValue(quad('UPGR'), this.m_upgm, i);
    frame.AddValue(quad('CMUK'), this.m_cmuntKilled, i);
    frame.AddValue(quad('CSTK'), this.m_cstruKilled, i);
    frame.AddValue(quad('CMLS'), this.m_cmuntLost, i);
    frame.AddValue(quad('CSLS'), this.m_cstruLost, i);
    frame.AddValue(quad('TCAQ'), this.m_nTotalCreditsAcquired, i);
    frame.AddValue(quad('TCCN'), this.m_nTotalCreditsConsumed, i);
    frame.AddValue(quad('CURL'), this.m_cUpdatesRepairLast, i);
    frame.AddValue(quad('CSNC'), this.m_cStructsNeedCredits, i);
    frame.AddValue(quad('WFHN'), this.m_wfHandicap, i);
  }

  // game/Player.cpp:524-633 (knVerPlayerState 11). String caps mirror the C++ buffers
  // (kcbPlayerName 32, kcchObjectiveMax 80, info 350, status 15); the 0xff first-byte
  // marker distinguishes never-set (NULL) formal objectives from empty ones.
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 11) return false;
    this.m_wfUpgrades = pstm.ReadWord();
    this.m_side = pstm.ReadWord();
    this.m_sidmAllies = pstm.ReadWord();
    this.m_sidmDiscovered = pstm.ReadWord();
    this.m_tptDiscover.tx = (pstm.ReadWord() << 16) >> 16;
    this.m_tptDiscover.ty = (pstm.ReadWord() << 16) >> 16;
    this.m_szName = pstm.ReadString(32);
    this.m_wf = pstm.ReadWord();
    this.m_pid = pstm.ReadWord();
    this.m_nCredits = pstm.ReadDword() | 0;
    this.m_nCreditsAcquired = 0;
    this.m_nCreditsConsumed = 0;
    this.m_nPowerSupply = (pstm.ReadWord() << 16) >> 16;
    this.m_nPowerDemand = (pstm.ReadWord() << 16) >> 16;
    for (let i = 0; i < this.m_acut.length; i++) this.m_acut[i] = pstm.ReadDword() | 0;
    this.m_umAllowed = pstm.ReadDword();
    this.m_szObjective = pstm.ReadString(80);
    this.m_upgm = pstm.ReadWord();
    this.m_upgmAllowed = pstm.ReadWord();
    this.m_cUpdatesRepairLast = pstm.ReadDword() | 0;

    const info = pstm.ReadString(350);
    this.m_szFormalObjectiveInfo = info.charCodeAt(0) === 0xff ? null : info;
    for (let i = 0; i < 4; i++) {
      const text = pstm.ReadString(80);
      this.m_aszFormalObjectives[i] = text.charCodeAt(0) === 0xff ? null : text;
      const status = pstm.ReadString(15);
      this.m_aszFormalObjectiveStatus[i] = status.charCodeAt(0) === 0xff ? null : status;
    }

    this.m_cmuntKilled = pstm.ReadWord();
    this.m_cstruKilled = pstm.ReadWord();
    this.m_cmuntLost = pstm.ReadWord();
    this.m_cstruLost = pstm.ReadWord();
    this.m_nTotalCreditsAcquired = pstm.ReadDword() | 0;
    this.m_nTotalCreditsConsumed = pstm.ReadDword() | 0;
    this.m_wfHandicap = pstm.ReadWord();

    // issue #96 (snapshotComplete): restore the per-cycle economy accumulators the faithful
    // path reset/omitted above (m_nCreditsAcquired/Consumed were zeroed at lines ~215-216).
    if (this.ctx.snapshotComplete) {
      this.m_nCreditsAcquired = pstm.ReadDword() | 0;
      this.m_nCreditsConsumed = pstm.ReadDword() | 0;
      this.m_nDirCredits = pstm.ReadDword() | 0;
      this.m_nConsumerCredits = pstm.ReadDword() | 0;
      for (let i = 0; i < this.m_acutBuilt.length; i++) this.m_acutBuilt[i] = pstm.ReadDword() | 0;
      this.m_loadedNeedCredits = this.m_cStructsNeedCredits = pstm.ReadDword() | 0;
      this.m_cUpdates = pstm.ReadDword() | 0;
      this.m_nLagState = pstm.ReadWord();
      this.m_fLeftGame = pstm.ReadByte() !== 0;
    }

    // the local player is the first human player loaded
    if (!(this.m_wf & kfPlrComputer) && this.ctx.plrLocal === null) {
      this.ctx.plrLocal = this;
    }
    return pstm.IsSuccess();
  }

  // issue #96 / #98: re-apply the snapshot's saved m_cStructsNeedCredits AFTER gobs are placed —
  // each loaded structure's ModifyNeedCreditsCount(+1) re-registration would otherwise double-
  // count past it (a from-scratch sim registered each structure exactly once). No-op for a
  // faithful load (the value was never stashed).
  ApplyLoadedNeedCredits(): void {
    if (this.m_loadedNeedCredits === null) return;
    this.m_cStructsNeedCredits = this.m_loadedNeedCredits;
    this.m_loadedNeedCredits = null;
  }

  // game/Player.cpp:635-686
  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(11);
    pstm.WriteWord(this.m_wfUpgrades);
    pstm.WriteWord(this.m_side);
    pstm.WriteWord(this.m_sidmAllies);
    pstm.WriteWord(this.m_sidmDiscovered);
    pstm.WriteWord(this.m_tptDiscover.tx);
    pstm.WriteWord(this.m_tptDiscover.ty);
    pstm.WriteString(this.m_szName);
    pstm.WriteWord(this.m_wf);
    pstm.WriteWord(this.m_pid);
    pstm.WriteDword(this.m_nCredits);
    pstm.WriteWord(this.m_nPowerSupply);
    pstm.WriteWord(this.m_nPowerDemand);
    for (let i = 0; i < this.m_acut.length; i++) pstm.WriteDword(this.m_acut[i]);
    pstm.WriteDword(this.m_umAllowed);
    pstm.WriteString(this.m_szObjective);
    pstm.WriteWord(this.m_upgm);
    pstm.WriteWord(this.m_upgmAllowed);
    pstm.WriteDword(this.m_cUpdatesRepairLast);

    const wrNullable = (sz: string | null) => {
      if (sz === null) {
        pstm.WriteByte(0xff);
        pstm.WriteByte(0);
      } else pstm.WriteString(sz);
    };
    wrNullable(this.m_szFormalObjectiveInfo);
    for (let i = 0; i < 4; i++) {
      wrNullable(this.m_aszFormalObjectives[i]);
      wrNullable(this.m_aszFormalObjectiveStatus[i]);
    }

    pstm.WriteWord(this.m_cmuntKilled);
    pstm.WriteWord(this.m_cstruKilled);
    pstm.WriteWord(this.m_cmuntLost);
    pstm.WriteWord(this.m_cstruLost);
    pstm.WriteDword(this.m_nTotalCreditsAcquired);
    pstm.WriteDword(this.m_nTotalCreditsConsumed);
    pstm.WriteWord(this.m_wfHandicap);
    // issue #96 (snapshotComplete): the C++/faithful load RESETS or omits these per-cycle
    // economy accumulators (+ stats / MP-lag fields) — fine for an SP savegame, but a
    // from-scratch sim has accumulated them, so they drive the periodic economy recalc and
    // must be carried for bit-exact resume.
    if (this.ctx.snapshotComplete) {
      pstm.WriteDword(this.m_nCreditsAcquired);
      pstm.WriteDword(this.m_nCreditsConsumed);
      pstm.WriteDword(this.m_nDirCredits);
      pstm.WriteDword(this.m_nConsumerCredits);
      for (let i = 0; i < this.m_acutBuilt.length; i++) pstm.WriteDword(this.m_acutBuilt[i]);
      pstm.WriteDword(this.m_cStructsNeedCredits);
      pstm.WriteDword(this.m_cUpdates);
      pstm.WriteWord(this.m_nLagState & 0xffff);
      pstm.WriteByte(this.m_fLeftGame ? 1 : 0);
    }
    return pstm.IsSuccess();
  }

  // ---- ht.h:5690-5878 inlines -------------------------------------------------
  GetId(): number {
    return this.m_pid;
  }
  GetSide(): number {
    return this.m_side;
  }
  SetSide(side: number): void {
    this.m_side = side;
    this.m_sidmAllies = GetSideMask(side);
    this.m_sidmDiscovered = GetSideMask(side);
  }
  GetAllies(): number {
    return this.m_sidmAllies;
  }
  SetAllies(sidm: number): void {
    this.m_sidmAllies = sidm | GetSideMask(this.m_side);
  }
  GetDiscoveredSides(): number {
    return this.m_sidmDiscovered;
  }
  SetDiscoveredSides(sidm: number): void {
    this.m_sidmDiscovered = sidm;
  }
  SetDiscoverPoint(tpt: { tx: number; ty: number }): void {
    this.m_tptDiscover = { tx: tpt.tx, ty: tpt.ty };
  }
  GetDiscoverPoint(): { tx: number; ty: number } {
    return this.m_tptDiscover;
  }
  GetCredits(): number {
    return this.m_nCredits;
  }
  GetNeedCreditsCount(): number {
    return this.m_cStructsNeedCredits & 0xffff;
  }
  GetCapacity(): number {
    return (
      this.m_acut[kutWarehouse] * knWarehouseCapacity +
      this.m_acut[kutProcessor] * knProcessorCapacity
    );
  }
  GetPowerSupply(): number {
    return this.m_nPowerSupply;
  }
  GetPowerDemand(): number {
    return this.m_nPowerDemand;
  }
  IsPowerLow(): boolean {
    return this.m_nPowerDemand > this.m_nPowerSupply;
  }
  IncUnitCount(ut: number): void {
    this.m_acut[ut]++;
  }
  DecUnitCount(ut: number): void {
    this.m_acut[ut]--;
  }
  GetUnitCount(ut: number): number {
    return this.m_acut[ut];
  }
  IncUnitBuiltCount(ut: number): void {
    this.m_acutBuilt[ut]++;
  }
  DecUnitBuiltCount(ut: number): void {
    this.m_acutBuilt[ut]--;
    if (this.m_acutBuilt[ut] < 0) this.m_acutBuilt[ut] = 0;
  }
  GetUpgrades(): number {
    return this.m_wfUpgrades;
  }
  GetAllowedUnits(): number {
    return this.m_umAllowed;
  }
  SetAllowedUnits(um: number): void {
    this.m_umAllowed = um >>> 0;
  }
  GetObjective(): string {
    return this.m_szObjective;
  }
  SetObjective(psz: string): void {
    this.m_szObjective = psz;
  }
  // game/Player.cpp formal objectives (kcFormalObjectivesMax=4) — text shown by the
  // objectives screen; set by trigger actions, never hashed
  // NULL vs "" is a real distinction in the save format (0xff marker = never set)
  m_aszFormalObjectives: (string | null)[] = [null, null, null, null];
  m_aszFormalObjectiveStatus: (string | null)[] = [null, null, null, null];
  // (m_szFormalObjectiveInfo is declared with the other fields above)
  // Player.cpp:986-993 — setting the text ALSO resets the status to "Incomplete"
  SetFormalObjectiveText(i: number, psz: string): void {
    this.m_aszFormalObjectives[i] = psz;
    this.SetFormalObjectiveStatus(i, 'Incomplete');
  }
  SetFormalObjectiveStatus(i: number, psz: string): void {
    this.m_aszFormalObjectiveStatus[i] = psz;
  }
  SetFormalObjectiveInfo(psz: string): void {
    this.m_szFormalObjectiveInfo = psz;
  }
  GetUpgradeMask(): number {
    return this.m_upgm;
  }
  SetUpgradeMask(upgm: number): void {
    this.m_upgm = upgm;
  }
  GetAllowedUpgrades(): number {
    return this.m_upgmAllowed;
  }
  SetAllowedUpgrades(um: number): void {
    this.m_upgmAllowed = um;
  }
  IncEnemyMobileUnitsKilled(): void {
    this.m_cmuntKilled++;
  }
  GetEnemyMobileUnitsKilled(): number {
    return this.m_cmuntKilled;
  }
  IncEnemyStructuresKilled(): void {
    this.m_cstruKilled++;
  }
  GetEnemyStructuresKilled(): number {
    return this.m_cstruKilled;
  }
  IncMobileUnitsLost(): void {
    this.m_cmuntLost++;
  }
  GetMobileUnitsLost(): number {
    return this.m_cmuntLost;
  }
  IncStructuresLost(): void {
    this.m_cstruLost++;
  }
  GetStructuresLost(): number {
    return this.m_cstruLost;
  }
  GetTotalCreditsAcquired(): number {
    return this.m_nTotalCreditsAcquired;
  }
  ModifyTotalCreditsConsumed(nAmount: number): void {
    this.m_nTotalCreditsConsumed += nAmount;
  }
  GetTotalCreditsConsumed(): number {
    return this.m_nTotalCreditsConsumed;
  }
  GetHandicap(): number {
    return this.m_wfHandicap;
  }
  SetHandicap(wf: number): void {
    this.m_wfHandicap = wf;
  }
  GetUpdateCount(): number {
    return this.m_cUpdates;
  }
  SetUpdateCount(cUpdates: number): void {
    this.m_cUpdates = cUpdates;
  }
  GetFlags(): number {
    return this.m_wf;
  }
  SetFlags(wf: number): void {
    this.m_wf = wf & 0xffff;
  }
  SetBot(fBot: boolean): void {
    this.m_fBot = fBot;
  }
  GetLeftGame(): boolean {
    return this.m_fLeftGame;
  } // ht.h:5654
  SetLeftGame(f: boolean): void {
    this.m_fLeftGame = f;
  } // ht.h:5651
  GetFormalObjectiveText(i: number): string | null {
    return this.m_aszFormalObjectives[i];
  } // ht.h:5657
  GetFormalObjectiveStatus(i: number): string | null {
    return this.m_aszFormalObjectiveStatus[i];
  } // ht.h:5661
  GetFormalObjectiveInfo(): string | null {
    return this.m_szFormalObjectiveInfo;
  } // ht.h:5665
  IsBot(): boolean {
    return this.m_fBot;
  }
  GetName(): string {
    return this.m_szName;
  }
  SetName(pszName: string): void {
    this.m_szName = pszName;
  } // Player.cpp:837-840

  // game/Player.cpp:759-796 — upgrade flags drive the upgm mask + unit notifications
  SetUpgrades(wfUpgrades: number): void {
    const wfChange = (wfUpgrades ^ this.m_wfUpgrades) & 0xffff;
    if (wfChange === 0) return;
    this.m_wfUpgrades = wfUpgrades & 0xffff;

    if (wfUpgrades & kfUpgradeHrc) this.m_upgm |= kupgmAdvancedHRC;
    else this.m_upgm &= ~kupgmAdvancedHRC;
    if (wfUpgrades & kfUpgradeVts) this.m_upgm |= kupgmAdvancedVTS;
    else this.m_upgm &= ~kupgmAdvancedVTS;

    const gobm = this.ctx.gobm;
    for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
      if (pgob.GetOwner() !== this) continue;
      if ((pgob.GetFlags() & (kfGobActive | kfGobUnit)) === (kfGobActive | kfGobUnit)) {
        switch (pgob.GetType()) {
          case kgtVehicleTransportStation:
            if (wfChange & kfUpgradeVtsInProgress) {
              this.ctx.smm!.SendMsgIds(
                this.m_wfUpgrades & kfUpgradeVtsInProgress
                  ? kmidBeingUpgraded
                  : kmidUpgradeComplete,
                pgob.GetId(),
              );
            }
            break;
          case kgtHumanResourceCenter:
            if (wfChange & kfUpgradeHrcInProgress) {
              this.ctx.smm!.SendMsgIds(
                this.m_wfUpgrades & kfUpgradeHrcInProgress
                  ? kmidBeingUpgraded
                  : kmidUpgradeComplete,
                pgob.GetId(),
              );
            }
            break;
        }
      }
    }
  }

  // game/Player.cpp:798-806
  ModifyNeedCreditsCount(cDelta: number): void {
    this.m_cStructsNeedCredits += cDelta;
  }

  // game/Player.cpp:808-835 — power-low transitions notify interested own units
  AddPowerSupplyAndDemand(nPowerSupply: number, nPowerDemand: number): void {
    const fLowBefore = this.IsPowerLow();
    this.m_nPowerSupply += nPowerSupply;
    this.m_nPowerDemand += nPowerDemand;
    const fLowAfter = this.IsPowerLow();

    if (fLowBefore !== fLowAfter) {
      const gobm = this.ctx.gobm;
      for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
        if (pgob.GetOwner() !== this) continue;
        if ((pgob.GetFlags() & (kfGobActive | kfGobUnit)) === (kfGobActive | kfGobUnit)) {
          const punt = pgob as UnitGobLike;
          if (punt.GetConsts().wf & kfUntcNotifyPowerLowHigh) {
            this.ctx.smm!.SendMsgIds(kmidPowerLowHigh, punt.GetId());
          }
        }
      }
      // gpmm->CalcPoweredRadar() for the LOCAL player is minimap presentation → host
      if (this === this.ctx.plrLocal) this.ctx.host.CalcPoweredRadar();
    }
  }

  // game/Player.cpp:843-853 — needs gapuntc (UnitConsts table; ctx.unitConsts)
  GetUnitMask(): number {
    let um = 0;
    for (let i = 0; i < kutMax; i++) {
      if (this.m_acut[i] !== 0) um = (um | this.ctx.unitConsts[i].um) >>> 0;
    }
    return um;
  }

  // game/Player.cpp:854-863
  GetUnitActiveCountFromMask(um: number): number {
    let c = 0;
    for (let i = 0; i < kutMax; i++) {
      if ((this.ctx.unitConsts[i].um & um) >>> 0) c += this.m_acut[i];
    }
    return c;
  }

  // game/Player.cpp:865-881
  GetUnitInstanceCountFromMask(um: number): number {
    if (!(this.m_wf & kfPlrInUse)) return 0;
    let cUnits = 0;
    const gobm = this.ctx.gobm;
    for (let pgob = gobm.GetFirstGob(); pgob !== null; pgob = gobm.GetNextGob(pgob)) {
      if (pgob.GetFlags() & kfGobUnit) {
        const punt = pgob as UnitGobLike;
        if (punt.GetOwner() === this) {
          if ((punt.GetConsts().um & um) >>> 0) cUnits++;
        }
      }
    }
    return cUnits;
  }

  // game/Player.cpp:883-895 — machine-local UI accessors (read-and-reset)
  GetCreditsDirection(): number {
    const nDir = this.m_nDirCredits;
    this.m_nDirCredits = 0;
    return nDir;
  }
  GetCreditsConsumer(): number {
    const nConsumer = this.m_nConsumerCredits;
    this.m_nConsumerCredits = knConsumerMax;
    return nConsumer;
  }

  // game/Player.cpp:897-925
  SetCredits(nCredits: number, fAffectTotals: boolean, nConsumer = knConsumerGeneric): void {
    if (nConsumer < this.m_nConsumerCredits) this.m_nConsumerCredits = nConsumer;

    if (nCredits > this.m_nCredits && this.m_nCredits !== 0) {
      this.m_nDirCredits = 1;
    } else if (nCredits < this.m_nCredits && nCredits !== 0) {
      if (this.m_nDirCredits === 0) this.m_nDirCredits = -1;
    }

    const dCredits = nCredits - this.m_nCredits;
    if (dCredits > 0) this.m_nTotalCreditsAcquired += dCredits;
    else this.m_nTotalCreditsConsumed -= dCredits;

    this.m_nCredits = nCredits;

    if (this === this.ctx.plrLocal && fAffectTotals) {
      if (dCredits < 0) this.m_nCreditsConsumed += -dCredits;
      else if (dCredits > 0) this.m_nCreditsAcquired += dCredits;
    }
  }

  // game/Player.cpp:932-956 — kcupdRepair = 6
  Update(cUpdates: number): void {
    if (this.m_cUpdatesRepairLast === 0 || Math.abs(this.m_cUpdatesRepairLast - cUpdates) >= 6) {
      this.m_cUpdatesRepairLast = cUpdates;
      if (this.m_wf & kfPlrAutoRepair) this.Repair(true);
    }

    if (this.m_nCreditsConsumed >= knCreditDelta) {
      this.m_nCreditsConsumed %= knCreditDelta;
      this.ctx.host.PlaySfx(18); // ksfxGameCreditsDecreasing (soundeffects.h positional enum)
    }
    if (this.m_nCreditsAcquired >= knCreditDelta) {
      this.m_nCreditsAcquired %= knCreditDelta;
      this.ctx.host.PlaySfx(19); // ksfxGameCreditsIncreasing (soundeffects.h positional enum)
    }
  }

  // game/Player.cpp:958-984 — auto-repair sweep over own structures
  Repair(fOn: boolean): void {
    if (fOn) this.m_wf |= kfPlrAutoRepair;
    else this.m_wf &= ~kfPlrAutoRepair;

    const gobm = this.ctx.gobm;
    for (let pgobT = gobm.GetFirstGob(); pgobT !== null; pgobT = gobm.GetNextGob(pgobT)) {
      if (pgobT.GetOwner() !== this) continue;
      if ((pgobT.GetFlags() & (kfGobStructure | kfGobActive)) !== (kfGobStructure | kfGobActive))
        continue;

      const pstru = pgobT as UnitGobLike;
      if (fOn) {
        if (!(pstru.GetUnitFlags() & kfUnitRepairing)) {
          if (pstru.GetHealth() < divfx(pstru.GetConsts().GetArmorStrength(), itofx(3))) {
            pstru.Repair(true);
          }
        }
      } else {
        if (pstru.GetUnitFlags() & kfUnitRepairing) pstru.Repair(false);
      }
    }
  }
}

export class PlayerMgr {
  private ctx: SimContext;
  private m_aplr: Player[] = [];

  constructor(ctx: SimContext) {
    // game/Player.cpp:17-21
    this.ctx = ctx;
    this.Reset();
  }

  // game/Player.cpp:28-36 — fresh zeroed Player array; clears the local pointer
  // game/Player.cpp:28-36 — Reset news the Player array and then MEMSETS IT TO ZERO,
  // wiping the constructors' Init(0) state. Unused slots therefore have side 0,
  // handicap 0 (NO kfHcapIncreasedArmor), umAllowed 0 — and GetPlayer(ksideNeutral=0)
  // resolves neutral level gobs to the first such ZEROED slot. A neutral tower takes
  // FULL damage in the shipping client; with ctor defaults it would take 87.5%
  // (caught by the m_05 cross-validation: neutral Junkyard towers died later in TS).
  Reset(): void {
    this.m_aplr = [];
    for (let i = 0; i < kcPlayersMax; i++) {
      const pplr = new Player(this.ctx);
      pplr.ZeroAsReset();
      this.m_aplr.push(pplr);
    }
    this.ctx.plrLocal = null;
  }

  // game/Player.cpp:38-52
  AllocPlayer(wf = 0): Player | null {
    for (let i = 0; i < kcPlayersMax; i++) {
      const pplr = this.m_aplr[i];
      if (pplr.m_wf & kfPlrInUse) continue;
      pplr.Init(i);
      pplr.m_wf |= kfPlrInUse | wf;
      return pplr;
    }
    return null;
  }

  // game/Player.cpp:241-269 (knVerPlayerMgrState 1)
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.Reset();
    let cPlayers = pstm.ReadWord();
    // issue #96 (snapshotComplete): ALL kcPlayersMax slots were saved in INDEX order — load
    // each straight into its slot so the NOT-IN-USE neutral player (m_aplr[4], wf=0) round-trips
    // its m_acut (unit counts) / power. The faithful path saves only in-use players and zeroes
    // the rest on Reset(), so a neutral-owned mission gob (e.g. a tower reading its owner's
    // radar count via GetUnitCount(kutRadar)) lost its owner's state and diverged.
    if (this.ctx.snapshotComplete) {
      for (let i = 0; i < cPlayers; i++) if (!this.m_aplr[i].LoadState(pstm)) return false;
      return true;
    }
    while (cPlayers-- !== 0) {
      const pplr = this.AllocPlayer();
      if (pplr === null || !pplr.LoadState(pstm)) return false;
    }
    return true;
  }

  // issue #96 / #98: called once gobs are placed (Level.LoadState) — re-apply each player's saved
  // m_cStructsNeedCredits over the gob-load re-registration. No-op for a faithful load.
  ApplyLoadedAccumulators(): void {
    for (const pplr of this.m_aplr) pplr.ApplyLoadedNeedCredits();
  }

  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    if (this.ctx.snapshotComplete) {
      // serialize EVERY slot (incl. the not-in-use neutral)
      pstm.WriteWord(this.m_aplr.length);
      for (const pplr of this.m_aplr) pplr.SaveState(pstm);
      return true;
    }
    pstm.WriteWord(this.GetPlayerCount());
    for (let pplr = this.GetNextPlayer(null); pplr !== null; pplr = this.GetNextPlayer(pplr)) {
      pplr.SaveState(pstm);
    }
    return true;
  }

  // ht.h:5948-5950
  FreePlayer(pplr: Player): void {
    pplr.m_wf = 0;
  }

  // game/Player.cpp:119-152 — count unique ally masks among active human players
  // (a mask-equality "team" count). fExtra exempts pidExtra from the computer/
  // unfulfilled filter (the player who just left still counts in the "before" tally).
  GetHumanTeamCount(fExtra: boolean, pidExtra: number): number {
    const asidmAlliesUnique: number[] = [];
    for (let i = 0; i < kcPlayersMax; i++) {
      const pplr = this.m_aplr[i];
      if ((pplr.m_wf & kfPlrInUse) === 0) continue;
      let fSkipCheck = false;
      if (fExtra && pplr.m_pid === pidExtra) fSkipCheck = true;
      if (!fSkipCheck) {
        if ((pplr.m_wf & (kfPlrInUse | kfPlrComputer | kfPlrUnfulfilled)) !== kfPlrInUse) continue;
      }
      if (!asidmAlliesUnique.includes(pplr.GetAllies())) {
        asidmAlliesUnique.push(pplr.GetAllies());
      }
    }
    return asidmAlliesUnique.length;
  }

  // game/Player.cpp:154-162 — true when the leaver's exit collapses the game to one team
  DetectTransitionToSingleHumanTeam(pidLeft: number): boolean {
    const cBefore = this.GetHumanTeamCount(true, pidLeft);
    const cAfter = this.GetHumanTeamCount(false, 0);
    return cAfter === 1 && cBefore > 1;
  }

  // game/Player.cpp:198-238 — the SP per-side allocation from the level's SideInfos
  // (the C++ loads a throwaway Level to read them; here the caller passes them in)
  InitFromSideInfos(asidi: { nIntelligence: number }[], wfHandicapLocal = 0): boolean {
    this.Reset();
    for (let i = 0; i < kcSides; i++) {
      const psidi = asidi[i];
      let wf = 0;
      switch (psidi.nIntelligence) {
        case knIntelligenceComputer:
        case knIntelligenceComputerNeutral:
          wf = kfPlrComputer;
          break;
        case knIntelligenceComputerOvermind:
          wf = kfPlrComputer | kfPlrComputerOvermind;
          break;
      }
      const pplr = this.AllocPlayer(wf);
      if (pplr === null) return false;
      // the local player is the first human allocated
      if (wf & kfPlrComputer) {
        pplr.SetHandicap(kfHcapHard); // levels are tuned for hard-handicap computers
      } else if (this.ctx.plrLocal === null) {
        this.ctx.plrLocal = pplr;
        pplr.SetHandicap(wfHandicapLocal);
      }
      pplr.SetSide(i);
    }
    return true;
  }

  // game/Player.cpp:54-64 — scans ALL slots (in-use or not); first side match wins
  GetPlayer(side: number): Player | null {
    for (let i = 0; i < kcPlayersMax; i++) {
      if (this.m_aplr[i].m_side === side) return this.m_aplr[i];
    }
    return null;
  }

  // game/Player.cpp:66-76
  GetNextPlayer(pplr: Player | null): Player | null {
    let i = pplr === null ? 0 : pplr.m_pid + 1;
    for (; i < kcPlayersMax; i++) {
      if (this.m_aplr[i].m_wf & kfPlrInUse) return this.m_aplr[i];
    }
    return null;
  }

  // game/Player.cpp:78-88
  GetNextHumanPlayer(pplr: Player | null): Player | null {
    let i = pplr === null ? 0 : pplr.m_pid + 1;
    for (; i < kcPlayersMax; i++) {
      if ((this.m_aplr[i].m_wf & (kfPlrInUse | kfPlrComputer)) === kfPlrInUse)
        return this.m_aplr[i];
    }
    return null;
  }

  // game/Player.cpp:90-105
  GetNextObservingPlayer(pplr: Player | null): Player | null {
    let i = pplr === null ? 0 : pplr.m_pid + 1;
    for (; i < kcPlayersMax; i++) {
      const p = this.m_aplr[i];
      if (p.m_fLeftGame) continue;
      if (
        (p.m_wf & (kfPlrInUse | kfPlrComputer | kfPlrObserver)) ===
        (kfPlrInUse | kfPlrComputer | kfPlrObserver)
      )
        return p;
    }
    return null;
  }

  // game/Player.cpp:107-117
  GetPlayerCount(): number {
    let cplr = 0;
    for (let i = 0; i < kcPlayersMax; i++) {
      if (this.m_aplr[i].m_wf & kfPlrInUse) cplr++;
    }
    return cplr;
  }

  // ht.h:5948-5960 inlines
  GetPlayerFromPid(pid: number): Player | null {
    if (pid !== 0xffff && pid < kcPlayersMax) return this.m_aplr[pid];
    return null;
  }

  // issue #96 (snapshotComplete owner encoding): a gob owner saved by PID cannot round-trip,
  // because the DUMMY (ctx.plrDummy) and the NEUTRAL player (the zeroed m_aplr slot that holds
  // neutral mission gobs) BOTH have pid 0 — GetPlayerFromPid(0) collapses them onto m_aplr[0]
  // (the real first player). The complete snapshot instead encodes the owner by its m_aplr
  // INDEX (which round-trips: LoadState AllocPlayers into the same slots and Reset zeroes the
  // rest), with 0xffff reserved for the dummy. The faithful saveState() still writes the pid.
  SnapshotOwnerCode(pplr: PlayerLike): number {
    if (pplr === this.ctx.plrDummy) return 0xffff;
    const i = this.m_aplr.indexOf(pplr as Player);
    return i >= 0 ? i : 0xffff; // unknown owner -> dummy (defensive; never expected)
  }

  PlayerFromSnapshotOwnerCode(code: number): PlayerLike {
    if (code === 0xffff || code >= this.m_aplr.length) return this.ctx.plrDummy;
    return this.m_aplr[code];
  }

  // game/Player.cpp (MP roster init, decoded form of PlayersUpdateNetMessage —
  // game/Player.cpp:163-194; the kfPlrr* wire-flag decode happens at the boundary)
  InitFromRoster(
    roster: {
      pid: number;
      side: number;
      name: string;
      ready?: boolean;
      computer?: boolean;
      overmind?: boolean;
      unfulfilled?: boolean;
      creator?: boolean;
      bot?: boolean;
      local?: boolean;
    }[],
  ): void {
    this.Reset();
    for (const r of roster) {
      const pplr = this.m_aplr[r.pid];
      pplr.Init(r.pid);
      pplr.m_wf |= kfPlrInUse;
      pplr.SetSide(r.side);
      pplr.m_szName = r.name;
      if (r.ready) pplr.m_wf |= kfPlrReady;
      if (r.computer) pplr.m_wf |= kfPlrComputer;
      if (r.overmind) pplr.m_wf |= kfPlrComputerOvermind;
      if (r.unfulfilled) pplr.m_wf |= kfPlrUnfulfilled;
      if (r.creator) pplr.m_wf |= kfPlrCreator;
      pplr.SetBot(!!r.bot);
      if (r.local) this.ctx.plrLocal = pplr;
      pplr.SetHandicap(kfHcapHard); // "life is hard for everybody in MP"
    }
  }

  // game/Player.cpp:261-292 — set allies then recalc every unit's enemy-nearby state
  SetAllies(applr: Player[], sidmAllies: number): void {
    for (const p of applr) {
      p.SetAllies(sidmAllies);
      // the C++ sends the RAW sidmAllies to the server (Player.cpp:270), NOT
      // the own-side-merged p.GetAllies() — the DO's team-chat routing
      // (game.ts setAllies) must see the same mask bytes either client sends
      this.ctx.host.UpdateAllies(p.GetSide(), sidmAllies);
    }
    const gobm = this.ctx.gobm;
    for (let pgobT = gobm.GetFirstGob(); pgobT !== null; pgobT = gobm.GetNextGob(pgobT)) {
      if (!(pgobT.GetFlags() & kfGobUnit)) continue;
      (pgobT as Gob & { RecalcEnemyNearby(fClear: boolean): void }).RecalcEnemyNearby(true);
    }
    for (let pgobT = gobm.GetFirstGob(); pgobT !== null; pgobT = gobm.GetNextGob(pgobT)) {
      if (!(pgobT.GetFlags() & kfGobUnit)) continue;
      (pgobT as Gob & { RecalcEnemyNearby(fClear: boolean): void }).RecalcEnemyNearby(false);
    }
  }

  // game/Player.cpp:327-335
  Update(cUpdates: number): void {
    for (let i = 0; i < kcPlayersMax; i++) {
      if (this.m_aplr[i].m_wf & kfPlrInUse) this.m_aplr[i].Update(cUpdates);
    }
  }

  // game/Player.cpp:336-348 — kfPlrComputer must match in BOTH (XOR trick)
  GetUnitInstanceCountFromMask(um: number, wfPlr: number): number {
    let cUnits = 0;
    for (let i = 0; i < kcPlayersMax; i++) {
      const pplr = this.m_aplr[i];
      if (((pplr.m_wf ^ wfPlr) & kfPlrComputer) === 0) {
        cUnits += pplr.GetUnitInstanceCountFromMask(um);
      }
    }
    return cUnits;
  }

  // game/Player.cpp:349-360 — ascending-pid order (hash-load-bearing)
  TrackState(frame: StateFrame): void {
    for (let i = 0; i < kcPlayersMax; i++) {
      const pplr = this.m_aplr[i];
      if (!(pplr.m_wf & kfPlrInUse)) continue;
      pplr.TrackState(frame);
    }
  }
}
